import React, { useState, useEffect, useCallback, useMemo } from 'react';
import V2IconButton from '../../../../components/button/v2_IconButton';
import V2Icon from '../../../../components/icon/v2_Icon';
import V2Loader from '../../../../components/loader/v2_Loader';
import V2GenericDrawer from '../../../../components/drawer/v2_GenericDrawer';
import V2DropdownSelect from '../../../../components/dropdown/V2DropdownSelect';
import dashboardApi from '../../../../services/dashboardApi';
import './V2TraceabilityTab.css';

const STREAM_GROUP_OPTIONS = [
  { value: 'discovery', label: 'Discovery' },
  { value: 'requirements', label: 'Requirements' },
  { value: 'design', label: 'Design' },
  { value: 'development', label: 'Development' },
  { value: 'testing', label: 'Testing' },
  { value: 'deployment', label: 'Deployment' },
];

const PROCESS_GROUP_OPTIONS = [
  { value: 'initiation', label: 'Initiation' },
  { value: 'planning', label: 'Planning' },
  { value: 'execution', label: 'Execution' },
  { value: 'monitoring', label: 'Monitoring' },
  { value: 'closure', label: 'Closure' },
];

const PRIORITY_FILTER_OPTIONS = [
  { value: 'all', label: 'Priority: All' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const RISK_FILTER_OPTIONS = [
  { value: 'all', label: 'Risk: All' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

/**
 * V2 Traceability Tab (RTM) - Requirements traceability matrix
 * 
 * Features:
 * - Title row with refresh control
 * - KPI strip with coverage stats (Total, Fully Traced, Partially Traced, At Risk)
 * - Trace Overview section (6 coverage metrics with progress bars)
 * - Issue spotlight (interactive clickable chips)
 * - Table controls (view toggle, search, filters)
 * - Expanded trace table (11 columns)
 * - Requirement detail drawer
 */
function V2TraceabilityTab({ workspaceId }) {
  const [rtmData, setRtmData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  
  // Chunk 3: Issue Spotlight - active filter state
  const [activeIssueId, setActiveIssueId] = useState(null);
  
  // Chunk 4: Table controls state
  const [viewMode, setViewMode] = useState('stream'); // 'stream' | 'process'
  const [searchQuery, setSearchQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [riskFilter, setRiskFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('all'); // Chunk 7: Group filter
  
  // Collapse state for hierarchical table
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [collapsedSubgroups, setCollapsedSubgroups] = useState(new Set());

  // Get coverage badge variant
  const getCoverageBadgeVariant = (percentage) => {
    if (percentage >= 70) return 'high';
    if (percentage >= 40) return 'medium';
    return 'low';
  };
  
  // Get score chip class (for trace_score)
  const getScoreChipClass = (score) => {
    if (score >= 90) return 'rtm-score-chip--great';
    if (score >= 70) return 'rtm-score-chip--good';
    if (score >= 40) return 'rtm-score-chip--warn';
    return 'rtm-score-chip--risk';
  };
  
  // Get coverage chip class (for percentages)
  const getCoverageChipClass = (pct) => {
    if (pct >= 70) return 'rtm-score-chip--great';
    if (pct >= 40) return 'rtm-score-chip--warn';
    return 'rtm-score-chip--risk';
  };
  
  // Calculate average score for a group of requirements
  const calcAvgScore = (reqs) => {
    if (!reqs.length) return 0;
    return Math.round(reqs.reduce((s, r) => s + (r.trace_score || 0), 0) / reqs.length);
  };
  
  // Get asset count for a requirement
  const getAssetCount = (req, assetType) => {
    const assets = req.linked_assets?.[assetType] || [];
    return Array.isArray(assets) ? assets.length : assets;
  };

  // Get progress bar color (Chunk 2)
  const getProgressColor = (percentage) => {
    if (percentage >= 70) return 'var(--icon-success)'; // Green
    if (percentage >= 40) return 'var(--icon-warning)'; // Yellow
    return 'var(--icon-danger)'; // Red
  };

  // Get severity styling (Chunk 3)
  const getSeverityClass = (severity) => {
    switch (severity) {
      case 'critical': return 'rtm-issue-chip--critical';
      case 'high': return 'rtm-issue-chip--high';
      case 'medium': return 'rtm-issue-chip--medium';
      case 'low': return 'rtm-issue-chip--low';
      default: return '';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'help_outline';
      default: return 'info';
    }
  };

  // Count total linked assets
  const countLinkedAssets = (linkedAssetsObj) => {
    if (!linkedAssetsObj || typeof linkedAssetsObj !== 'object') return 0;
    
    return Object.values(linkedAssetsObj).reduce((total, value) => {
      // Handle both array format and count format
      if (Array.isArray(value)) {
        return total + value.length;
      }
      if (typeof value === 'number') {
        return total + value;
      }
      return total;
    }, 0);
  };

  // Fetch RTM data
  const fetchRTMData = useCallback(async () => {
    if (!workspaceId) {
      console.log('⚠️ V2TraceabilityTab: No workspaceId provided');
      return;
    }

    console.log('🔍 V2TraceabilityTab: Fetching RTM data for workspace:', workspaceId);
    setLoading(true);
    
    try {
      const response = await dashboardApi.getRTMData(workspaceId);
      console.log('✅ V2TraceabilityTab: API response:', response);

      const data = response.data?.data || response.data || null;
      setRtmData(data);
      console.log('📊 V2TraceabilityTab: RTM data loaded:', data);
    } catch (error) {
      console.error('❌ V2TraceabilityTab: Error fetching RTM data:', error);
      setRtmData(null);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  // Initial fetch
  useEffect(() => {
    fetchRTMData();
  }, [fetchRTMData]);

  // Handle refresh
  const handleRefresh = () => {
    fetchRTMData();
  };

  // Handle view requirement details
  const handleViewRequirement = (requirement) => {
    setSelectedRequirement(requirement);
    setShowDrawer(true);
  };

  // Chunk 3: Handle issue chip click (filter table)
  const handleIssueClick = (issueId) => {
    setActiveIssueId(activeIssueId === issueId ? null : issueId);
  };
  
  // Toggle group expand/collapse
  const toggleGroup = useCallback((id) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Toggle subgroup expand/collapse
  const toggleSubgroup = useCallback((id) => {
    setCollapsedSubgroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  
  // Collapse all groups when view mode changes
  useEffect(() => {
    const allGroups = new Set();
    const allSubgroups = new Set();

    if (viewMode === 'stream') {
      (rtmData?.streams || []).forEach((s) => allGroups.add(s.stream_id));
      (rtmData?.meetings || []).forEach((m) => allSubgroups.add(m.meeting_id));
    } else {
      (rtmData?.processes || []).forEach((p) => allGroups.add(p.process_id));
      (rtmData?.process_steps || []).forEach((s) => allSubgroups.add(s.step_id));
    }
    allGroups.add('unmatched');

    setCollapsedGroups(allGroups);
    setCollapsedSubgroups(allSubgroups);
  }, [viewMode, rtmData]);

  // Chunk 4: Filter and search requirements
  const filteredRequirements = useMemo(() => {
    let filtered = rtmData?.requirements || [];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(req => 
        req.req_id?.toLowerCase().includes(query) ||
        req.title?.toLowerCase().includes(query)
      );
    }

    // Apply priority filter
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(req => req.priority?.toLowerCase() === priorityFilter);
    }

    // Apply risk filter
    if (riskFilter !== 'all') {
      filtered = filtered.filter(req => req.risk_level?.toLowerCase() === riskFilter);
    }
    
    // Chunk 7: Apply group filter (stream or process)
    if (groupFilter !== 'all') {
      filtered = filtered.filter(req => {
        if (viewMode === 'stream') {
          return req.stream_name?.toLowerCase() === groupFilter.toLowerCase();
        } else {
          return req.process_name?.toLowerCase() === groupFilter.toLowerCase();
        }
      });
    }

    // Apply issue spotlight filter
    if (activeIssueId) {
      const issue = (rtmData?.issue_spotlight || []).find(i => i.id === activeIssueId);
      if (issue?.filter_field && issue?.filter_value !== undefined) {
        filtered = filtered.filter(req => req[issue.filter_field] === issue.filter_value);
      }
    }

    return filtered;
  }, [rtmData, searchQuery, priorityFilter, riskFilter, groupFilter, viewMode, activeIssueId]);

  // Calculate coverage percentage for each asset type (for column headers)
  const columnCoverage = useMemo(() => {
    const result = {};
    const assetTypes = ['stories', 'test_cases', 'design_docs', 'defects', 'uat_evidence', 'deployment'];
    
    assetTypes.forEach((assetType) => {
      if (!filteredRequirements.length) { 
        result[assetType] = 0; 
        return; 
      }
      
      const covered = filteredRequirements.filter((req) => {
        const assets = req.linked_assets?.[assetType] || [];
        const count = Array.isArray(assets) ? assets.length : assets;
        return count > 0;
      }).length;
      
      result[assetType] = Math.round((covered / filteredRequirements.length) * 100);
    });
    
    return result;
  }, [filteredRequirements]);
  
  // Build hierarchical grouped rows (Stream -> Meeting -> Req OR Process -> Step -> Req)
  const groupedRows = useMemo(() => {
    const rows = [];
    const ASSET_TYPES = ['stories', 'test_cases', 'design_docs', 'defects', 'uat_evidence', 'deployment'];
    
    // Compute coverage for a group of requirements
    const computeCoverage = (reqs) => {
      const result = {};
      ASSET_TYPES.forEach((at) => {
        if (!reqs.length) { 
          result[at] = 0; 
          return; 
        }
        const covered = reqs.filter(
          (req) => {
            const assets = req.linked_assets?.[at] || [];
            const count = Array.isArray(assets) ? assets.length : assets;
            return count > 0;
          }
        ).length;
        result[at] = Math.round((covered / reqs.length) * 100);
      });
      return result;
    };

    const pushGroup = (id, label, icon, groupReqs) => {
      rows.push({ 
        type: 'group', 
        id, 
        label, 
        icon, 
        reqCount: groupReqs.length, 
        avg: calcAvgScore(groupReqs), 
        assetCoverage: computeCoverage(groupReqs) 
      });
    };
    
    const pushSubgroup = (id, label, icon, subReqs, parentGroupId) => {
      rows.push({ 
        type: 'subgroup', 
        id, 
        label, 
        icon, 
        reqCount: subReqs.length, 
        avg: calcAvgScore(subReqs), 
        parentGroupId, 
        assetCoverage: computeCoverage(subReqs) 
      });
    };

    const streams = rtmData?.streams || [];
    const meetings = rtmData?.meetings || [];
    const processes = rtmData?.processes || [];
    const processSteps = rtmData?.process_steps || [];

    if (viewMode === 'stream') {
      // Stream-based grouping
      streams.forEach((stream) => {
        const streamMeetings = meetings.filter((m) => m.stream_id === stream.stream_id);
        const streamReqs = filteredRequirements.filter((r) => r.stream_id === stream.stream_id);
        if (streamReqs.length === 0) return;
        
        pushGroup(stream.stream_id, stream.name, 'account_tree', streamReqs);
        
        streamMeetings.forEach((mtg) => {
          const mtgReqs = streamReqs.filter((r) => r.meeting_id === mtg.meeting_id);
          if (mtgReqs.length === 0) return;
          pushSubgroup(mtg.meeting_id, `${mtg.title} — ${mtg.date}`, 'event', mtgReqs, stream.stream_id);
          mtgReqs.forEach((req) => rows.push({ type: 'req', req, groupId: stream.stream_id, subgroupId: mtg.meeting_id }));
        });
        
        // Requirements not in any meeting
        const ungrouped = streamReqs.filter(
          (r) => !streamMeetings.find((m) => m.meeting_id === r.meeting_id)
        );
        ungrouped.forEach((req) => rows.push({ type: 'req', req, groupId: stream.stream_id, subgroupId: null }));
      });
    } else {
      // Process-based grouping
      processes.forEach((proc) => {
        const procSteps = processSteps.filter((st) => st.process_id === proc.process_id);
        const procReqs = filteredRequirements.filter((r) => r.process_id === proc.process_id);
        if (procReqs.length === 0) return;
        
        pushGroup(proc.process_id, proc.name, 'account_tree', procReqs);
        
        procSteps.forEach((step) => {
          const stepReqs = procReqs.filter((r) => r.process_step_id === step.step_id);
          if (stepReqs.length === 0) return;
          pushSubgroup(step.step_id, `${step.order}. ${step.title}`, 'play_arrow', stepReqs, proc.process_id);
          stepReqs.forEach((req) => rows.push({ type: 'req', req, groupId: proc.process_id, subgroupId: step.step_id }));
        });
        
        // Requirements not in any step
        const ungrouped = procReqs.filter(
          (r) => !procSteps.find((s) => s.step_id === r.process_step_id)
        );
        ungrouped.forEach((req) => rows.push({ type: 'req', req, groupId: proc.process_id, subgroupId: null }));
      });
    }
    
    // Unmatched requirements (no stream/process assignment)
    const matched = new Set(rows.filter((r) => r.type === 'req').map((r) => r.req.req_id));
    const unmatched = filteredRequirements.filter((r) => !matched.has(r.req_id));
    if (unmatched.length > 0) {
      pushGroup('unmatched', 'Other Requirements', 'folder', unmatched);
      unmatched.forEach((req) => rows.push({ type: 'req', req, groupId: 'unmatched', subgroupId: null }));
    }
    
    return rows;
  }, [viewMode, filteredRequirements, rtmData, calcAvgScore]);

  const groupFilterOptions = useMemo(() => [
    { value: 'all', label: `${viewMode === 'stream' ? 'Stream:' : 'Process:'} All` },
    ...(viewMode === 'stream' ? STREAM_GROUP_OPTIONS : PROCESS_GROUP_OPTIONS),
  ], [viewMode]);

  // Loading state
  if (loading) {
    return (
      <div className="v2-rtm-loading">
        <V2Loader size="large" />
        <p className="v2-rtm-loading__text">Loading traceability data...</p>
      </div>
    );
  }

  // Calculate KPIs (Chunk 1: Updated to use kpi_summary)
  const kpis = rtmData?.kpi_summary || {};
  const totalRequirements = kpis.total_requirements || 0;
  const fullyTraced = kpis.fully_traced || 0;
  const partiallyTraced = kpis.partially_traced || 0;
  const atRisk = kpis.at_risk || 0;
  
  const coverageTrend = rtmData?.coverage_trend || [];
  const issues = rtmData?.issue_spotlight || [];

  return (
    <div className="v2-rtm-tab">
      {/* Title Row */}
      <div className="v2-rtm-title-row">
        <h2 className="v2-rtm-title">Requirements Traceability Matrix</h2>
        <div className="v2-rtm-controls">
          <V2IconButton 
            name="refresh" 
            onClick={handleRefresh}
            title="Refresh"
          />
        </div>
      </div>

      {/* Chunk 1: Fixed KPI Strip (Remove Overall Coverage, Add At Risk) */}
      <div className="v2-rtm-kpi-strip">
        {/* Card 1: Total Requirements */}
        <div className="v2-rtm-kpi-card">
          <div className="v2-rtm-kpi-card__header">
            <V2Icon name="assignment" className="v2-rtm-kpi-card__icon" />
            <div className="v2-rtm-kpi-card__label">Total Requirements</div>
          </div>
          <div className="v2-rtm-kpi-card__value">{totalRequirements}</div>
        </div>
        
        {/* Card 2: Fully Traced */}
        <div className="v2-rtm-kpi-card">
          <div className="v2-rtm-kpi-card__header">
            <V2Icon name="check_circle" className="v2-rtm-kpi-card__icon v2-rtm-kpi-card__icon--success" />
            <div className="v2-rtm-kpi-card__label">Fully Traced</div>
          </div>
          <div className="v2-rtm-kpi-card__value">{fullyTraced}</div>
          <div className="v2-rtm-kpi-card__progress">
            <div 
              className="v2-rtm-kpi-card__progress-bar v2-rtm-kpi-card__progress-bar--success"
              style={{ width: `${totalRequirements > 0 ? (fullyTraced / totalRequirements * 100) : 0}%` }}
            />
          </div>
        </div>
        
        {/* Card 3: Partially Traced */}
        <div className="v2-rtm-kpi-card">
          <div className="v2-rtm-kpi-card__header">
            <V2Icon name="pending" className="v2-rtm-kpi-card__icon v2-rtm-kpi-card__icon--warning" />
            <div className="v2-rtm-kpi-card__label">Partially Traced</div>
          </div>
          <div className="v2-rtm-kpi-card__value">{partiallyTraced}</div>
          <div className="v2-rtm-kpi-card__progress">
            <div 
              className="v2-rtm-kpi-card__progress-bar v2-rtm-kpi-card__progress-bar--warning"
              style={{ width: `${totalRequirements > 0 ? (partiallyTraced / totalRequirements * 100) : 0}%` }}
            />
          </div>
        </div>
        
        {/* Card 4: At Risk (NEW - Chunk 1) */}
        <div className="v2-rtm-kpi-card v2-rtm-kpi-card--at-risk">
          <div className="v2-rtm-kpi-card__header">
            <V2Icon name="warning" className="v2-rtm-kpi-card__icon v2-rtm-kpi-card__icon--danger" />
            <div className="v2-rtm-kpi-card__label">At Risk</div>
          </div>
          <div className="v2-rtm-kpi-card__value v2-rtm-kpi-card__value--danger">{atRisk}</div>
        </div>
      </div>

      {/* Chunk 2: Trace Overview Section */}
      {coverageTrend.length > 0 && (
        <div className="v2-rtm-trace-overview">
          <div className="v2-rtm-section-title">
            <V2Icon name="insights" className="v2-rtm-section-icon" />
            <span>Trace Overview</span>
          </div>
          <div className="v2-rtm-coverage-grid">
            {coverageTrend.map((item) => {
              const pct = item.pct || 0;
              const statusClass = pct >= 70 ? 'healthy' : pct >= 40 ? 'warning' : 'risk';
              return (
                <div key={item.asset_type} className="v2-rtm-coverage-item">
                  <div className="v2-rtm-coverage-header">
                    <span className="v2-rtm-coverage-label">{item.label}</span>
                    <span className={`v2-rtm-coverage-pct v2-rtm-coverage-pct--${statusClass}`}>
                      {pct}%
                    </span>
                  </div>
                  <div className="v2-rtm-coverage-bar-track">
                    <div 
                      className="v2-rtm-coverage-bar-fill"
                      style={{ 
                        width: `${pct}%`,
                        backgroundColor: getProgressColor(pct)
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Chunk 3: Issue Spotlight Section */}
      {issues.length > 0 && (
        <div className="v2-rtm-issue-spotlight-section">
          <div className="v2-rtm-section-title">
            <V2Icon name="crisis_alert" className="v2-rtm-section-icon" />
            <span>Issue Spotlight</span>
          </div>

          {/* Active Filter Banner */}
          {activeIssueId && (
            <div className="v2-rtm-active-filter-banner">
              <V2Icon name="filter_alt" />
              <span>Filtering by: {issues.find(i => i.id === activeIssueId)?.message}</span>
              <button 
                className="v2-rtm-clear-filter-btn"
                onClick={() => handleIssueClick(null)}
              >
                <V2Icon name="close" />
                Clear filter
              </button>
            </div>
          )}

          {/* Issue Chips */}
          <div className="v2-rtm-issue-chips-grid">
            {issues.map((issue) => {
              const isActive = activeIssueId === issue.id;
              return (
                <button
                  key={issue.id}
                  className={`v2-rtm-issue-chip ${getSeverityClass(issue.severity)} ${isActive ? 'v2-rtm-issue-chip--active' : ''}`}
                  onClick={() => handleIssueClick(issue.id)}
                  type="button"
                >
                  <div className="v2-rtm-issue-chip-header">
                    <V2Icon name={getSeverityIcon(issue.severity)} className="v2-rtm-issue-icon" />
                    <span className="v2-rtm-issue-count">{issue.count}</span>
                  </div>
                  <div className="v2-rtm-issue-message">{issue.message}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Chunk 4: Table Controls */}
      <div className="v2-rtm-table-controls">
        {/* View Mode Toggle */}
        <div className="v2-rtm-view-toggle">
          <button
            className={`v2-rtm-toggle-btn ${viewMode === 'stream' ? 'v2-rtm-toggle-btn--active' : ''}`}
            onClick={() => setViewMode('stream')}
            type="button"
          >
            Stream
          </button>
          <button
            className={`v2-rtm-toggle-btn ${viewMode === 'process' ? 'v2-rtm-toggle-btn--active' : ''}`}
            onClick={() => setViewMode('process')}
            type="button"
          >
            Process
          </button>
        </div>

        {/* Search Box */}
        <div className="v2-rtm-search-box">
          <V2Icon name="search" className="v2-rtm-search-icon" />
          <input
            type="text"
            placeholder="Search requirements..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="v2-rtm-search-input"
          />
          {searchQuery && (
            <button
              className="v2-rtm-search-clear"
              onClick={() => setSearchQuery('')}
              type="button"
            >
              <V2Icon name="close" />
            </button>
          )}
        </div>

        {/* Filter Dropdowns */}
        <div className="v2-rtm-filters">
          {/* Chunk 7: Group Filter */}
          <V2DropdownSelect
            variant="plain"
            options={groupFilterOptions}
            value={groupFilter}
            onChange={setGroupFilter}
          />

          <V2DropdownSelect
            variant="plain"
            options={PRIORITY_FILTER_OPTIONS}
            value={priorityFilter}
            onChange={setPriorityFilter}
          />

          <V2DropdownSelect
            variant="plain"
            options={RISK_FILTER_OPTIONS}
            value={riskFilter}
            onChange={setRiskFilter}
          />
        </div>
      </div>

      {/* Chunk 5: Hierarchical Cascading Table */}
      <div className="v2-rtm-table-wrapper">
        <table className="v2-rtm-table">
          <thead>
            <tr>
              <th className="v2-rtm-th v2-rtm-th-sticky" style={{ width: '260px', minWidth: '260px' }}>Name / ID</th>
              <th className="v2-rtm-th" style={{ width: '90px', textAlign: 'center' }}>Score</th>
              <th className="v2-rtm-th" style={{ width: '110px', textAlign: 'center' }}>Status</th>
              <th className="v2-rtm-th" style={{ width: '80px', textAlign: 'center' }}>Priority</th>
              <th className="v2-rtm-th" style={{ width: '80px', textAlign: 'center' }}>
                <div className="rtm-th-asset-inner">
                  <span>Stories</span>
                  <span className={`rtm-score-chip rtm-th-coverage-chip ${getCoverageChipClass(columnCoverage.stories ?? 0)}`}>
                    {columnCoverage.stories ?? 0}%
                  </span>
                </div>
              </th>
              <th className="v2-rtm-th" style={{ width: '80px', textAlign: 'center' }}>
                <div className="rtm-th-asset-inner">
                  <span>Tests</span>
                  <span className={`rtm-score-chip rtm-th-coverage-chip ${getCoverageChipClass(columnCoverage.test_cases ?? 0)}`}>
                    {columnCoverage.test_cases ?? 0}%
                  </span>
                </div>
              </th>
              <th className="v2-rtm-th" style={{ width: '80px', textAlign: 'center' }}>
                <div className="rtm-th-asset-inner">
                  <span>Design</span>
                  <span className={`rtm-score-chip rtm-th-coverage-chip ${getCoverageChipClass(columnCoverage.design_docs ?? 0)}`}>
                    {columnCoverage.design_docs ?? 0}%
                  </span>
                </div>
              </th>
              <th className="v2-rtm-th" style={{ width: '80px', textAlign: 'center' }}>
                <div className="rtm-th-asset-inner">
                  <span>Defects</span>
                  <span className={`rtm-score-chip rtm-th-coverage-chip ${getCoverageChipClass(columnCoverage.defects ?? 0)}`}>
                    {columnCoverage.defects ?? 0}%
                  </span>
                </div>
              </th>
              <th className="v2-rtm-th" style={{ width: '80px', textAlign: 'center' }}>
                <div className="rtm-th-asset-inner">
                  <span>UAT</span>
                  <span className={`rtm-score-chip rtm-th-coverage-chip ${getCoverageChipClass(columnCoverage.uat_evidence ?? 0)}`}>
                    {columnCoverage.uat_evidence ?? 0}%
                  </span>
                </div>
              </th>
              <th className="v2-rtm-th" style={{ width: '80px', textAlign: 'center' }}>
                <div className="rtm-th-asset-inner">
                  <span>Deploy</span>
                  <span className={`rtm-score-chip rtm-th-coverage-chip ${getCoverageChipClass(columnCoverage.deployment ?? 0)}`}>
                    {columnCoverage.deployment ?? 0}%
                  </span>
                </div>
              </th>
              <th className="v2-rtm-th" style={{ width: '80px' }}></th>
            </tr>
          </thead>
          <tbody>
            {groupedRows.length === 0 ? (
              <tr>
                <td colSpan="11" className="v2-rtm-empty-message">
                  {searchQuery || priorityFilter !== 'all' || riskFilter !== 'all' || activeIssueId
                    ? 'No requirements match your filters'
                    : 'No requirements found'}
                </td>
              </tr>
            ) : (
              groupedRows.map((row, idx) => {
                // ── Group row ──
                if (row.type === 'group') {
                  const isCollapsed = collapsedGroups.has(row.id);
                  return (
                    <tr key={`g-${row.id}-${idx}`} className="v2-rtm-group-row" onClick={() => toggleGroup(row.id)}>
                      <td className="v2-rtm-td v2-rtm-td-sticky v2-rtm-group-cell">
                        <div className="v2-rtm-group-inner">
                          <V2Icon name={isCollapsed ? 'chevron_right' : 'expand_more'} className="v2-rtm-group-chevron" />
                          <V2Icon name={row.icon} className="v2-rtm-group-icon" />
                          <span className="v2-rtm-group-label">{row.label}</span>
                        </div>
                      </td>
                      <td className="v2-rtm-td" style={{ textAlign: 'center' }}>
                        {isCollapsed && (
                          <span className={`rtm-score-chip ${getScoreChipClass(row.avg)}`}>{row.avg}</span>
                        )}
                      </td>
                      <td className="v2-rtm-td"></td>
                      <td className="v2-rtm-td"></td>
                      {['stories', 'test_cases', 'design_docs', 'defects', 'uat_evidence', 'deployment'].map((at) => (
                        <td key={at} className="v2-rtm-td v2-rtm-group-coverage-cell">
                          {isCollapsed && (
                            <span className={`rtm-score-chip ${getCoverageChipClass(row.assetCoverage?.[at] ?? 0)}`}>
                              {row.assetCoverage?.[at] ?? 0}%
                            </span>
                          )}
                        </td>
                      ))}
                      <td className="v2-rtm-td v2-rtm-group-req-count">
                        <span>{row.reqCount} req{row.reqCount !== 1 ? 's' : ''}</span>
                      </td>
                    </tr>
                  );
                }

                // Skip subgroups/reqs inside collapsed group
                if (row.type === 'subgroup' && collapsedGroups.has(row.parentGroupId)) return null;
                if (row.type === 'req' && collapsedGroups.has(row.groupId)) return null;

                // ── Subgroup row ──
                if (row.type === 'subgroup') {
                  const isCollapsed = collapsedSubgroups.has(row.id);
                  return (
                    <tr key={`sg-${row.id}-${idx}`} className="v2-rtm-subgroup-row" onClick={() => toggleSubgroup(row.id)}>
                      <td className="v2-rtm-td v2-rtm-td-sticky v2-rtm-subgroup-cell">
                        <div className="v2-rtm-group-inner v2-rtm-subgroup-inner">
                          <V2Icon name={isCollapsed ? 'chevron_right' : 'expand_more'} className="v2-rtm-group-chevron" />
                          <V2Icon name={row.icon} className="v2-rtm-group-icon" />
                          <span className="v2-rtm-group-label">{row.label}</span>
                        </div>
                      </td>
                      <td className="v2-rtm-td" style={{ textAlign: 'center' }}>
                        {isCollapsed && (
                          <span className={`rtm-score-chip ${getScoreChipClass(row.avg)}`}>{row.avg}</span>
                        )}
                      </td>
                      <td className="v2-rtm-td"></td>
                      <td className="v2-rtm-td"></td>
                      {['stories', 'test_cases', 'design_docs', 'defects', 'uat_evidence', 'deployment'].map((at) => (
                        <td key={at} className="v2-rtm-td v2-rtm-subgroup-coverage-cell">
                          {isCollapsed && (
                            <span className={`rtm-score-chip ${getCoverageChipClass(row.assetCoverage?.[at] ?? 0)}`}>
                              {row.assetCoverage?.[at] ?? 0}%
                            </span>
                          )}
                        </td>
                      ))}
                      <td className="v2-rtm-td v2-rtm-group-req-count">
                        <span>{row.reqCount} req{row.reqCount !== 1 ? 's' : ''}</span>
                      </td>
                    </tr>
                  );
                }

                // Skip reqs inside collapsed subgroup
                if (row.subgroupId && collapsedSubgroups.has(row.subgroupId)) return null;

                // ── Requirement row ──
                const { req } = row;
                return (
                  <tr key={req.req_id} className="v2-rtm-req-row" onClick={() => handleViewRequirement(req)}>
                    <td className="v2-rtm-td v2-rtm-td-sticky" style={{ width: '260px' }}>
                      <div className="v2-rtm-req-name-cell">
                        <span className="v2-rtm-req-id">{req.req_id}</span>
                        <span className="v2-rtm-req-title" title={req.title}>{req.title}</span>
                        {req.risk_level === 'High' && (
                          <span className="v2-rtm-risk-badge">High Risk</span>
                        )}
                      </div>
                    </td>
                    <td className="v2-rtm-td" style={{ textAlign: 'center' }}>
                      <span className={`rtm-score-chip ${getScoreChipClass(req.trace_score || 0)}`}>
                        {req.trace_score || 0}
                      </span>
                    </td>
                    <td className="v2-rtm-td" style={{ textAlign: 'center' }}>
                      <span className="rtm-status-badge">{req.status || 'N/A'}</span>
                    </td>
                    <td className="v2-rtm-td" style={{ textAlign: 'center' }}>
                      <span className={`rtm-priority-badge rtm-priority-badge--${req.priority?.toLowerCase() || 'medium'}`}>
                        {req.priority || 'Medium'}
                      </span>
                    </td>
                    {['stories', 'test_cases', 'design_docs', 'defects', 'uat_evidence', 'deployment'].map((at) => {
                      const count = getAssetCount(req, at);
                      return (
                        <td key={at} className="v2-rtm-td" style={{ textAlign: 'center' }}>
                          {count > 0 ? (
                            <span className="v2-rtm-asset-count">{count}</span>
                          ) : (
                            <span className="v2-rtm-asset-empty">—</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="v2-rtm-td" style={{ textAlign: 'center' }}>
                      <V2IconButton 
                        name="visibility"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewRequirement(req);
                        }}
                        title="View Details"
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Chunk 6: Enhanced Requirement Detail Drawer */}
      <V2GenericDrawer
        isOpen={showDrawer}
        onClose={() => setShowDrawer(false)}
        title="Requirement Details"
        width="700px"
      >
        {selectedRequirement && (
          <div style={{ padding: 'var(--spacing-300)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-400)' }}>
            {/* Header Section */}
            <div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start',
                marginBottom: 'var(--spacing-200)'
              }}>
                <div>
                  <div style={{ 
                    fontSize: 'var(--font-size-body-xs)', 
                    color: 'var(--text-subtle)',
                    marginBottom: 'var(--spacing-50)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Requirement ID
                  </div>
                  <div style={{ 
                    fontSize: 'var(--font-size-heading-s)', 
                    color: 'var(--text-default)',
                    fontWeight: 'var(--font-weight-semibold)'
                  }}>
                    {selectedRequirement.req_id || '—'}
                  </div>
                </div>
                {selectedRequirement.risk_level && (
                  <div style={{
                    padding: '6px 12px',
                    borderRadius: '8px',
                    background: selectedRequirement.risk_level === 'high' ? 'var(--background-accent-red-subtler-default)' : 
                               selectedRequirement.risk_level === 'medium' ? 'var(--background-accent-orange-subtler-default)' :
                               'var(--background-accent-green-subtler-default)',
                    color: selectedRequirement.risk_level === 'high' ? 'var(--text-danger)' : 
                           selectedRequirement.risk_level === 'medium' ? 'var(--text-warning)' :
                           'var(--text-success)',
                    fontSize: 'var(--font-size-body-s)',
                    fontWeight: 'var(--font-weight-semibold)',
                    textTransform: 'capitalize',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-50)'
                  }}>
                    <V2Icon name="warning" size="sm" />
                    {selectedRequirement.risk_level} Risk
                  </div>
                )}
              </div>
              <div style={{ 
                fontSize: 'var(--font-size-body-m)', 
                color: 'var(--text-default)',
                lineHeight: '1.5'
              }}>
                {selectedRequirement.title || '—'}
              </div>
            </div>
            
            {/* Coverage Score */}
            <div style={{
              background: 'var(--elevation-surface-raised-default)',
              padding: 'var(--spacing-200)',
              borderRadius: '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ fontSize: 'var(--font-size-body-m)', color: 'var(--text-default)' }}>
                Traceability Score
              </div>
              <span className={`v2-rtm-coverage-badge v2-rtm-coverage-badge--${getCoverageBadgeVariant(selectedRequirement.coverage_percentage || 0)}`}>
                {selectedRequirement.coverage_percentage || 0}%
              </span>
            </div>
            
            {/* Linked Assets Breakdown */}
            <div>
              <h3 style={{ 
                fontSize: 'var(--font-size-heading-xs)', 
                color: 'var(--text-default)',
                fontWeight: 'var(--font-weight-semibold)',
                marginBottom: 'var(--spacing-200)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-100)'
              }}>
                <V2Icon name="link" size="sm" />
                Linked Assets ({countLinkedAssets(selectedRequirement.linked_assets || {})})
              </h3>
              {selectedRequirement.linked_assets && typeof selectedRequirement.linked_assets === 'object' ? (
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 'var(--spacing-150)'
                }}>
                  {[
                    {
                      key: 'stories',
                      label: 'User Stories',
                      icon: 'description',
                      color: 'var(--color-accent-blue)',
                      borderColor: 'var(--border-brand)'
                    },
                    {
                      key: 'test_cases',
                      label: 'Test Cases',
                      icon: 'fact_check',
                      color: 'var(--color-accent-magenta)',
                      borderColor: 'var(--color-accent-magenta)'
                    },
                    {
                      key: 'design_docs',
                      label: 'Design Docs',
                      icon: 'architecture',
                      color: 'var(--icon-information)',
                      borderColor: 'var(--border-information)'
                    },
                    {
                      key: 'defects',
                      label: 'Defects',
                      icon: 'bug_report',
                      color: 'var(--icon-danger)',
                      borderColor: 'var(--border-danger)'
                    },
                    {
                      key: 'uat_evidence',
                      label: 'UAT Evidence',
                      icon: 'verified',
                      color: 'var(--icon-success)',
                      borderColor: 'var(--border-success)'
                    },
                    {
                      key: 'deployment',
                      label: 'Deployment',
                      icon: 'rocket_launch',
                      color: 'var(--color-accent-magenta)',
                      borderColor: 'var(--color-accent-magenta)'
                    }
                  ].map(asset => (
                    <div key={asset.key} style={{
                      padding: 'var(--spacing-150)',
                      background: 'var(--elevation-surface-raised-default)',
                      border: `1px solid ${asset.borderColor}`,
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-100)' }}>
                        <V2Icon name={asset.icon} size="sm" style={{ color: asset.color }} />
                        <span style={{ fontSize: 'var(--font-size-body-s)', color: 'var(--text-default)' }}>
                          {asset.label}
                        </span>
                      </div>
                      <span style={{ 
                        fontSize: 'var(--font-size-body-m)', 
                        color: asset.color,
                        fontWeight: 'var(--font-weight-semibold)'
                      }}>
                        {Array.isArray(selectedRequirement.linked_assets[asset.key]) 
                          ? selectedRequirement.linked_assets[asset.key].length
                          : (selectedRequirement.linked_assets[asset.key] || 0)
                        }
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ 
                  padding: 'var(--spacing-300)',
                  background: 'var(--elevation-surface-raised-default)',
                  borderRadius: '8px',
                  textAlign: 'center',
                  color: 'var(--text-subtle)',
                  fontSize: 'var(--font-size-body-s)'
                }}>
                  No linked assets
                </div>
              )}
            </div>
            
            {/* Audit Log Section */}
            {selectedRequirement.audit_log && selectedRequirement.audit_log.length > 0 && (
              <div>
                <h3 style={{ 
                  fontSize: 'var(--font-size-heading-xs)', 
                  color: 'var(--text-default)',
                  fontWeight: 'var(--font-weight-semibold)',
                  marginBottom: 'var(--spacing-200)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-100)'
                }}>
                  <V2Icon name="history" size="sm" />
                  Audit Log
                </h3>
                <div style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--spacing-100)',
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}>
                  {selectedRequirement.audit_log.slice(0, 5).map((log, idx) => (
                    <div key={idx} style={{
                      padding: 'var(--spacing-150)',
                      background: 'var(--elevation-surface-raised-default)',
                      borderRadius: '8px',
                      fontSize: 'var(--font-size-body-s)'
                    }}>
                      <div style={{ color: 'var(--text-default)', marginBottom: 'var(--spacing-50)' }}>
                        {log.action || 'Activity'}
                      </div>
                      <div style={{ color: 'var(--text-subtle)' }}>
                        {log.user} • {log.timestamp ? new Date(log.timestamp).toLocaleString() : 'Recently'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* AI Recommendations */}
            {selectedRequirement.ai_recommendations && selectedRequirement.ai_recommendations.length > 0 && (
              <div>
                <h3 style={{ 
                  fontSize: 'var(--font-size-heading-xs)', 
                  color: 'var(--text-default)',
                  fontWeight: 'var(--font-weight-semibold)',
                  marginBottom: 'var(--spacing-200)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-100)'
                }}>
                  <V2Icon name="psychology" size="sm" style={{ color: 'var(--color-accent-magenta)' }} />
                  AI Recommendations
                </h3>
                <div style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--spacing-150)'
                }}>
                  {selectedRequirement.ai_recommendations.map((rec, idx) => (
                    <div key={idx} style={{
                      padding: 'var(--spacing-200)',
                      background: 'linear-gradient(135deg, var(--color-accent-magenta-alpha-10) 0%, var(--color-accent-blue-alpha-10) 100%)',
                      border: '1px solid var(--color-accent-magenta)',
                      borderRadius: '8px'
                    }}>
                      <div style={{ 
                        color: 'var(--text-default)', 
                        fontSize: 'var(--font-size-body-m)',
                        lineHeight: '1.5'
                      }}>
                        {rec.text || rec.recommendation}
                      </div>
                      {rec.confidence && (
                        <div style={{ 
                          marginTop: 'var(--spacing-100)',
                          fontSize: 'var(--font-size-body-xs)',
                          color: 'var(--text-subtle)'
                        }}>
                          Confidence: {rec.confidence}%
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Description */}
            {selectedRequirement.description && (
              <div>
                <h3 style={{ 
                  fontSize: 'var(--font-size-heading-xs)', 
                  color: 'var(--text-default)',
                  fontWeight: 'var(--font-weight-semibold)',
                  marginBottom: 'var(--spacing-150)'
                }}>
                  Description
                </h3>
                <div style={{ 
                  fontSize: 'var(--font-size-body-m)', 
                  color: 'var(--text-default)',
                  lineHeight: '1.6',
                  padding: 'var(--spacing-200)',
                  background: 'var(--elevation-surface-raised-default)',
                  borderRadius: '8px'
                }}>
                  {selectedRequirement.description}
                </div>
              </div>
            )}
          </div>
        )}
      </V2GenericDrawer>
    </div>
  );
}

export default V2TraceabilityTab;
